import { useEffect, useRef } from 'react';
import { useUIStore } from '@/stores';
import './TitleScreen.css';

/**
 * TitleScreen - タイトル画面
 *
 * フルスクリーンの背景演出と「Press Any Button」テキストを表示し、
 * 任意のキー入力またはクリックで MainMenu へ遷移する。
 *
 * 右上に歯車アイコンを配置し、設定モーダルへの導線を提供する。
 * 設定モーダルが開いている間は誤遷移しないようガードする。
 */
const TitleScreen = () => {
  const navigateTo = useUIStore((state) => state.navigateTo);
  const openSettings = useUIStore((state) => state.openSettings);
  const isSettingsOpen = useUIStore((state) => state.modals.settings.isOpen);

  // 重複発火防止フラグ (StrictMode の二重マウントや連続イベント対策)
  const hasNavigatedRef = useRef(false);
  // 設定モーダル状態を ref 経由で参照 (リスナーを再登録せずに最新値を取得)
  const isSettingsOpenRef = useRef(isSettingsOpen);

  useEffect(() => {
    isSettingsOpenRef.current = isSettingsOpen;
  }, [isSettingsOpen]);

  useEffect(() => {
    const handleAnyInput = (event: KeyboardEvent | PointerEvent) => {
      // 設定モーダルが開いている場合は遷移しない
      if (isSettingsOpenRef.current) {
        return;
      }
      // 既に遷移済みなら無視
      if (hasNavigatedRef.current) {
        return;
      }
      if (event instanceof KeyboardEvent) {
        // Tab はフォーカス移動なので遷移させない (歯車にキーボードで到達できるようにする)
        if (event.key === 'Tab') {
          return;
        }
        // 修飾キー単独押下では遷移させない (Shift/Control/Alt/Meta はショートカット入力途中)
        if (event.key === 'Shift' || event.key === 'Control' || event.key === 'Alt' || event.key === 'Meta') {
          return;
        }
      }
      // 歯車ボタン上での入力 (Tab+Enter 等のキーボード操作含む) は遷移を抑止
      // event.target はキーイベント時にフォーカス移動前の要素を指すため、
      // document.activeElement (フォーカス中要素) も併せて判定する二重防御
      const target = event.target as Element | null;
      const activeEl = document.activeElement;
      const isOnSettingsButton =
        (target && typeof target.closest === 'function' && target.closest('.TitleScreen__settings')) ||
        (activeEl && typeof activeEl.closest === 'function' && activeEl.closest('.TitleScreen__settings'));
      if (isOnSettingsButton) {
        return;
      }
      hasNavigatedRef.current = true;
      navigateTo('main_menu');
    };

    window.addEventListener('keydown', handleAnyInput);
    window.addEventListener('pointerdown', handleAnyInput);

    return () => {
      window.removeEventListener('keydown', handleAnyInput);
      window.removeEventListener('pointerdown', handleAnyInput);
    };
  }, [navigateTo]);

  /**
   * 歯車ボタンクリック: 設定モーダルを開く
   * stopPropagation でタイトル画面の Press Any Button 遷移を抑止
   */
  const handleSettingsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openSettings();
  };

  /**
   * 歯車ボタン上での pointerdown も親の遷移トリガーから守る
   * (window-level handler 側でも target 判定で二重防御している)
   */
  const handleSettingsPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };

  return (
    <div className="TitleScreen">
      {/* 右上の設定ボタン */}
      <button
        type="button"
        className="TitleScreen__settings"
        onClick={handleSettingsClick}
        onPointerDown={handleSettingsPointerDown}
        aria-label="設定"
      >
        <span aria-hidden="true">⚙️</span>
      </button>

      {/* タイトル */}
      <div className="TitleScreen__content">
        <img
          src="/assets/title/title_logo.png"
          alt="The Hunter and the Hunted"
          className="TitleScreen__logo"
          draggable={false}
        />
        <p className="TitleScreen__press">
          Press Any Button
        </p>
      </div>
    </div>
  );
};

export default TitleScreen;
